import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import ProductAdsTable from "../../../../components/custom/category/advertisements/ProductAdsTable";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { selectionStateToSelectedIds } from "../../../../components/table/utils";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../../utils/dashboardLinks";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { useGetV4ProductAdReportByDate } from "../../../../modules/perf_reports/api/product_ad/v4ProductAdReportByDate";
import { useGetV4ProductAdReportTotals } from "../../../../modules/perf_reports/api/product_ad/v4ProductAdReportTotals";

function ProductAdReportBody() {
  const { t } = useTranslation(["perf_reports"]);
  const acosOrRoas = useAcosOrRoas();
  const standardMetricIds = useStandardEntityMetrics("productAd");

  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  // Use persisted table state for filter synchronization with timeline/totals
  const { state } = usePersistedTableState({
    persistKey: "productAdReport",
    initialState: {
      columnVisibility: {},
      columnOrder: [],
      columnPinning: { start: ["product_title"], end: [] },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const filters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length > 0 ? [{ id: "product_id", value: { op: "anyof", value: selectedIds } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter];
  }, [state.columnFilters, selectedIds]);

  const byDateOptionsGetter = useGetV4ProductAdReportByDate();
  const productAdByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: filters,
      search: debounceSearch,
    }),
  );

  const totalsOptionsGetter = useGetV4ProductAdReportTotals();
  const productAdTotalsReportQuery = useQuery(
    totalsOptionsGetter({
      filters: filters,
      search: debounceSearch,
    }),
  );

  const timeLineData = useMemo(() => {
    return productAdByDateReportQuery.data?.data ?? [];
  }, [productAdByDateReportQuery.data?.data]);

  const metrics = useMemo(() => {
    return standardMetricIds.map((id) => getMetricSpec(id));
  }, [standardMetricIds]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"productAdReport"}
        title={t(($) => $.perf_reports["Performance Overview"])}
        totals={{
          data: productAdTotalsReportQuery.data?.data,
          loading:
            productAdTotalsReportQuery.isFetching &&
            (!productAdTotalsReportQuery.data || productAdTotalsReportQuery.isPlaceholderData),
        }}
        timeline={{
          data: timeLineData,
          loading:
            productAdByDateReportQuery.isFetching &&
            (!productAdByDateReportQuery.data || productAdByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"productAd"}
      >
        <ProductAdsTable showAdColumn persistKey="productAdReport" />
      </MetricsCard>
    </PageContainer>
  );
}

function ProductAdReport() {
  const { t } = useTranslation(["common"]);
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: t(($) => $.common.sidebar["Product Ad Report"]),
      href: "/user/report/product_ad",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <ProductAdReportBody />
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/report/product_ad")({
  component: ProductAdReport,
  staticData: { application: "Reports" },
});
